/**
 * An invented `GET /metrics/executive` answer: LOCAL DEV ONLY (served by `@/lib/data` when
 * the dev server cannot reach the endpoint) and the base of the dashboard tests.
 *
 * Every number is made up. The shape and the edge cases are real: a measured headline, a
 * thin area that is "Not enough data", an area with no quality figure at all, revision
 * mails not measured yet, the pilot not started, and a current week counted so far.
 */

import type { ExecArea, ExecShare, ExecutiveMetrics, ExecWeek } from "@/lib/api";

function shareOf(
  count: number,
  base: number,
  prevCount: number,
  prevBase: number,
  floor = 30,
): ExecShare {
  return {
    count,
    base,
    share: base > 0 ? count / base : null,
    lower_bound: null,
    enough: base >= floor,
    prev_count: prevCount,
    prev_base: prevBase,
    prev_share: prevBase > 0 ? prevCount / prevBase : null,
  };
}

const EMPTY = shareOf(0, 0, 0, 0);

function area(
  code: string,
  label: string,
  inquiries: number,
  figures: Partial<ExecArea>,
): ExecArea {
  return {
    area: code,
    label,
    inquiries,
    quotations: 0,
    thread: EMPTY,
    single: EMPTY,
    precision_items: EMPTY,
    precision_customer: EMPTY,
    lines_auto: EMPTY,
    truth_coverage: EMPTY,
    gate: "not_enough",
    trend: [],
    ...figures,
  };
}

/** Ten Mondays ending with the week of `today` (the last one counted so far). */
function trendWeeks(today: Date, count: number): string[] {
  const day = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate()));
  const monday = new Date(day.getTime() - ((day.getUTCDay() + 6) % 7) * 86_400_000);
  return Array.from({ length: count }, (_, index) =>
    new Date(monday.getTime() - (count - 1 - index) * 7 * 86_400_000).toISOString().slice(0, 10),
  );
}

const THREAD = [0.47, 0.49, 0.5, 0.48, 0.52, 0.51, 0.53, 0.55, 0.54, null];
const SINGLE = [0.4, 0.41, 0.43, 0.42, 0.45, 0.44, 0.46, 0.47, 0.46, null];
const PRECISION = [0.8, 0.81, 0.83, 0.82, 0.84, 0.85, 0.85, 0.86, 0.85, null];
const INQUIRIES = [610, 640, 655, 630, 660, 640, 690, 675, 682, 190];

export interface ExecutiveFixtureWindow {
  month?: string;
  periodWeeks?: number;
  weeks?: number;
  areas?: readonly string[];
}

export function getExecutiveFixture(
  window: ExecutiveFixtureWindow = {},
  today: Date = new Date(),
): ExecutiveMetrics {
  const weeks = trendWeeks(today, 10);
  const weekly: ExecWeek[] = weeks.map((week, index) => {
    const inquiries = INQUIRIES[index] ?? 0;
    const lines = Math.round(inquiries * 0.66);
    return {
      week,
      partial: index === weeks.length - 1,
      inquiries,
      ready: Math.round(inquiries * 0.05),
      lines_auto: Math.round(inquiries * 2.6 * 0.18),
      lines_total: Math.round(inquiries * 2.6),
      quotations: index === weeks.length - 1 ? 12 : 108,
      lines,
      thread_share: THREAD[index] ?? null,
      single_share: SINGLE[index] ?? null,
      precision_right: Math.round(105 * (PRECISION[index] ?? 0)),
      precision_total: index === weeks.length - 1 ? 0 : 105,
      precision_share: PRECISION[index] ?? null,
      one_click_right: 20,
      one_click_checked: 23,
    };
  });
  const areas: ExecArea[] = [
    area("dk", "Denmark", 612, {
      quotations: 160,
      thread: shareOf(372, 600, 330, 590),
      single: shareOf(294, 600, 280, 590),
      precision_items: shareOf(161, 167, 150, 158),
      precision_customer: shareOf(80, 80, 70, 71),
      lines_auto: shareOf(290, 1610, 260, 1580),
      truth_coverage: shareOf(160, 612, 150, 600),
      gate: "below",
      trend: [0.55, 0.58, 0.6, 0.59, 0.61, 0.6, 0.62, 0.63, 0.62, null],
    }),
    area("uk", "United Kingdom", 540, {
      quotations: 96,
      thread: shareOf(160, 320, 150, 310),
      single: shareOf(128, 320, 120, 310),
      precision_items: shareOf(58, 63, 50, 56),
      precision_customer: shareOf(70, 71, 64, 66),
      lines_auto: shareOf(127, 1400, 110, 1380),
      truth_coverage: shareOf(96, 540, 90, 520),
      gate: "below",
      trend: [0.45, 0.46, 0.48, 0.47, 0.5, 0.49, 0.5, 0.51, 0.5, null],
    }),
    area("usl", "USL", 720, {
      quotations: 150,
      thread: shareOf(398, 750, 380, 740),
      single: shareOf(330, 750, 320, 740),
      precision_items: shareOf(136, 186, 120, 170),
      precision_customer: shareOf(84, 87, 78, 80),
      lines_auto: shareOf(486, 1870, 450, 1820),
      truth_coverage: shareOf(150, 720, 140, 700),
      gate: "below",
      trend: [0.5, 0.5, 0.51, 0.52, 0.53, 0.52, 0.54, 0.55, 0.53, null],
    }),
    area("de", "Germany", 380, {
      quotations: 22,
      // 22 quotations: under the floor of 30, so "x of y" and no percentage.
      thread: shareOf(40, 70, 30, 60, 100),
      single: shareOf(34, 70, 26, 60, 100),
      precision_items: shareOf(11, 12, 9, 10),
      lines_auto: shareOf(188, 990, 170, 960),
      truth_coverage: shareOf(22, 380, 18, 360),
      gate: "not_enough",
    }),
    area("in", "India", 435, {
      lines_auto: shareOf(181, 1130, 160, 1100),
      gate: "not_enough",
    }),
  ].filter((row) => !window.areas?.length || window.areas.includes(row.area));

  const month = window.month;
  const start = month ? `${month}-01` : weeks[weeks.length - 5];
  const end = weeks[weeks.length - 1] ?? "";
  return {
    computed_at: today.toISOString(),
    period: { start: start ?? "", end, weeks: month ? null : (window.periodWeeks ?? 4), month },
    previous_period: {
      start: weeks[weeks.length - 9] ?? "",
      end: start ?? "",
      weeks: month ? null : (window.periodWeeks ?? 4),
    },
    trend_weeks: 10,
    areas_in_scope: areas.map((row) => row.area),
    measured_areas: ["dk", "uk", "usl"],
    headline: {
      agreement: {
        quotations: 432,
        lines: 1767,
        thread: shareOf(952, 1767, 880, 1730),
        single: shareOf(808, 1767, 770, 1730),
        prev_quotations: 418,
        revision: {
          measured: false,
          new_share: null,
          new_quotations: 0,
          revision_share: null,
          revision_quotations: 0,
          reason: "The quotation dates are not filled in yet.",
        },
      },
      precision: {
        items: shareOf(358, 420, 330, 400),
        items_split: shareOf(379, 420, 352, 400),
        customer: shareOf(234, 238, 220, 226),
      },
      inquiries: { count: 2687, per_week: 672, prev_count: 2585 },
      autonomy: {
        lines: shareOf(1272, 7000, 1150, 6860),
        inquiries_matched: shareOf(181, 2687, 160, 2585),
        inquiries_ready: shareOf(134, 2687, 120, 2585),
        right_in_fo: shareOf(81, 92, 70, 84),
      },
      speed: {
        prepared_n: 1840,
        prepared_median_min: 4,
        prepared_p90_min: 18,
        prev_prepared_median_min: null,
        live_since: "2026-10-07",
        reply_n: 723,
        reply_median_h: 4.1,
        prev_reply_median_h: 4.4,
        reply_areas: ["dk", "uk", "usl"],
      },
    },
    value: [
      { area: "dk", currency: "EUR", quotations: 160, quoted: 2_010_000, won: 410_000 },
      { area: "usl", currency: "USD", quotations: 150, quoted: 2_120_000, won: 600_000 },
      { area: "uk", currency: "GBP", quotations: 96, quoted: 250_000, won: 61_000 },
      { area: "de", currency: "EUR", quotations: 22, quoted: 360_000, won: 0 },
    ].filter((row) => !window.areas?.length || window.areas.includes(row.area)),
    weekly,
    areas,
    status: {
      health: "ok",
      import_stalled: false,
      last_import_at: new Date(today.getTime() - 2 * 60_000).toISOString(),
      master_data_ok: true,
      master_data_oldest_sync_at: new Date(today.getTime() - 21 * 60_000).toISOString(),
      send_blocked: true,
    },
    decisions: [
      {
        key: "close_no_activity",
        state: "on",
        text: "Close inquiries nobody worked on for 21 days, in I2Q only (Reopen brings one back). Switched on.",
      },
      {
        key: "revision_split",
        state: "not_measured",
        text: "Revision mails cannot be shown apart until the quotation dates are filled in (a data fix).",
      },
      {
        key: "quality_areas",
        state: "not_measured",
        text: "No quality figure yet for Germany and India: too few quotations were found in F&O.",
        effect_count: 2,
      },
      {
        key: "pilot",
        state: "not_started",
        text: "Pilot not started. Figures on cases worked in I2Q appear once sales work cases here.",
      },
    ],
    flags: { pilot_started: false, revision_split_measured: false, value_available: true },
    gate: {
      precision_bar: 0.995,
      coverage_bar: 0.75,
      min_lines: 100,
      min_quotations: 30,
      set_on: "Aug 2026",
    },
  };
}
